import {
  selectBuildingsDamage,
  selectCitiesCache,
  selectHexes,
  selectPendingSupply,
  selectPlayers,
  selectSelectedFarmsForConversion,
  selectSpecialBuildings,
  selectUnits
} from "../../../redux/reducers/sharedStateReducer/selectors";
import {calculateAvailableSupplyCells} from "../calculateAvailableSupplyCells";
import {calcFarmRemainingSupply} from "../calcFarmRemainingSupply";
import {checkIfHexBlockedByEnemyUnit} from "../checkIfHexBlockedByEnemyUnit";
import {getFarmsOfPlayerCities} from "../getPlayerFarms";
import {checkIfUnitHasPendingAdditionalSupply} from "../checkIfUnitHasPendingAdditionalSupply";
import {isUnitFaction} from "../../factions/isUnitFaction";
import {isPlayerFaction} from "../../factions/isPlayerFaction";
import {isPlayerPolicy} from "../../factions/isPlayerPolicy";
import {FACTIONS} from "../../../constants/factions";
import {POLICIES} from "../../../constants/policies";
import {TOKENS} from "../../../constants/tokens";
import {UNIT_TYPES} from "../../../configs/units";
import {checkSameTypeUnitRecruitmentLimit} from "../recruitment/checkSameTypeUnitRecruitmentLimit";
import {checkIfFinalRound} from "../../checkIfFinalRound";
import {getPlayerUnits} from "../../players/getPlayerUnits";
import {getGridGeometry} from "./getGridGeometry";

const NO_FARMS = {processedIds: [], actionableIds: []};

/**
 * Дела фазы снабжения игрока — его фермы, у которых в этой фазе есть
 * снабжение (ферма не заблокирована вражеским отрядом):
 *  - processedIds — фермы, раздавшие все снабжение;
 *  - actionableIds — фермы с нераспределенным снабжением, которое есть куда
 *    потратить.
 * Ферма с остатком, который некуда потратить, не входит ни в один список.
 *
 * Куда потратить остаток. Отряд в зоне фермы, который еще МОЖЕТ принять
 * снабжение: отряд, уже получивший базовое и дополнительное снабжение,
 * больше ничего не возьмёт (та же проверка, что и на клике по нему — см.
 * GameScene). Исключение — Великаны: у них нет капа на базовое снабжение,
 * поэтому для них хватает самой достижимости. Проверка по факции ОТРЯДА, а не
 * игрока — способность остаётся у Великана, захваченного Суккубом (см.
 * unit-scoped-faction-abilities).
 *
 * Кормление — не единственный способ потратить остаток:
 *  - Крысолюды меняют остаток СВОБОДНОЙ фермы на бесплатного d6 прямо на ней
 *    (RatmenRecruitmentToolbar) — достижимые отряды тут ни при чем;
 *  - с Аграризмом остаток ферм уходит в монеты (calcAgrarianismConversion),
 *    отряды тоже не нужны.
 *
 * Игрок передается явно: в одновременной фазе снабжения сетевой игры текущий
 * игрок — только якорь хода, а фермы нужны действующего игрока.
 *
 * @param state
 * @param player - чьи фермы считать
 * @param [hexGridRenderer] - ячейки поля; по умолчанию геометрия без рендерера
 * @returns {{processedIds: string[], actionableIds: string[]}}
 */
export const getSupplyFarms = (state, player, hexGridRenderer = getGridGeometry(state)) => {
  if (!player) {
    return NO_FARMS;
  }

  const hexes = selectHexes(state);
  const specialBuildings = selectSpecialBuildings(state);
  const units = selectUnits(state);
  const citiesCache = selectCitiesCache(state);
  const playerCities = (citiesCache || {})[player.color] || [];
  const buildingsDamage = selectBuildingsDamage(state);
  const selectedFarmsForConversion = selectSelectedFarmsForConversion(state, player.id);
  const players = selectPlayers(state);
  const pendingSupply = selectPendingSupply(state);
  const playerFarms = getFarmsOfPlayerCities(citiesCache, player.color);

  // Остаток можно потратить мимо отрядов: крысолюдам — на найм d6 на самой
  // ферме, аграристу — на монеты
  const canRecruitForSupply = isPlayerFaction(player, FACTIONS.RATMEN)
    && !checkIfFinalRound(state)
    && checkSameTypeUnitRecruitmentLimit(player, UNIT_TYPES.D6.id, getPlayerUnits(player, units));
  const canConvertToCoins = isPlayerPolicy(player, POLICIES.AGRARIANISM);

  const checkIfFarmCanSpend = (farmHexKey) => {
    if (canConvertToCoins) {
      return true;
    }

    // Найм крысолюдов идет на саму ферму — она должна быть свободна
    if (canRecruitForSupply && !units.some((unit) => unit.hexKey === farmHexKey)) {
      return true;
    }

    const availableSupplyCells = calculateAvailableSupplyCells(
      hexGridRenderer,
      hexes,
      specialBuildings,
      units,
      farmHexKey,
      player,
      playerCities,
      buildingsDamage,
    );

    return availableSupplyCells.some((cellKey) => {
      const unit = units.find((item) => item.hexKey === cellKey && item.ownerId === player.id);
      if (!unit) {
        return false;
      }
      return isUnitFaction(unit, FACTIONS.GIANTS)
        || !checkIfUnitHasPendingAdditionalSupply(unit.id, pendingSupply);
    });
  };

  const processedIds = [];
  const actionableIds = [];
  playerFarms.forEach((farmHexKey) => {
    // Заблокированная ферма в этой фазе снабжения не дает
    if (checkIfHexBlockedByEnemyUnit(
      farmHexKey, TOKENS.FARM, player.id, units, hexes, specialBuildings, buildingsDamage
    )) {
      return;
    }

    const remainingSupply = calcFarmRemainingSupply(
      farmHexKey, selectedFarmsForConversion, hexes, specialBuildings,
      players, units, citiesCache, player, pendingSupply, buildingsDamage
    );
    if (remainingSupply <= 0) {
      processedIds.push(farmHexKey);
    } else if (checkIfFarmCanSpend(farmHexKey)) {
      actionableIds.push(farmHexKey);
    }
  });

  return {processedIds, actionableIds};
};
